import React, { useEffect, useState, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { BreadCrumb } from "primereact/breadcrumb";
import SmilesDrawer from "smiles-drawer";
import agent from "../../../../app/api/agent";
import SmilesView from "../../../../app/common/SmilesView/SmilesView";
import SectionHeading from "../../../../app/common/SectionHeading/SectionHeading";
import KetcherDialog from "./KetcherDialog";
import "./SubTableView.css";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

// ── Recharts BarChart wrapper ──
const SimpleBarChart = ({ data, xKey, yKey, yLabel, groupKey, colors, config = {} }) => {
  if (!data || data.length === 0) return null;

  const {
    height = 300,
    barWidth = 20,
    barGap = 4,
    legendPos = "bottom",
    showGrid = true,
    showTooltip = true,
    showLegend = true,
    showLabel = true,
    colorSingle = "#6366f1",
    axisFontSize = 11,
  } = config;

  const commonProps = {
    margin: { top: 20, right: 30, left: 20, bottom: 60 },
    barCategoryGap: barGap,
    barSize: barWidth,
  };

  const axisProps = {
    tick: { fontSize: axisFontSize, fill: "#64748b" },
    angle: -30,
    textAnchor: "end",
    height: 60,
  };

  if (groupKey && colors) {
    const groups = [...new Set(data.map(d => d[xKey]))];
    const subGroups = [...new Set(data.map(d => d[groupKey]))];
    const chartData = groups.map(g => {
      const row = { [xKey]: g };
      subGroups.forEach(sg => {
        const item = data.find(d => d[xKey] === g && d[groupKey] === sg);
        row[sg] = item ? parseFloat(item[yKey]) : 0;
      });
      return row;
    });

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} {...commonProps}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
          <XAxis dataKey={xKey} {...axisProps} />
          <YAxis tick={{ fontSize: axisFontSize, fill: "#64748b" }} label={{ value: yLabel || yKey, angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#64748b" } }} />
          {showTooltip && <Tooltip />}
          {showLegend && <Legend verticalAlign={legendPos} />}
          {subGroups.map((sg, i) => (
            <Bar key={sg} dataKey={sg} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]}
              {...(showLabel ? { label: { position: "top", fontSize: 10, fill: "#334155" } } : {})}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const sorted = [...data].sort((a, b) => parseFloat(b[yKey]) - parseFloat(a[yKey]));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={sorted} {...commonProps}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
        <XAxis dataKey={xKey} {...axisProps} />
        <YAxis tick={{ fontSize: axisFontSize, fill: "#64748b" }} label={{ value: yLabel || yKey, angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#64748b" } }} />
        {showTooltip && <Tooltip />}
        <Bar dataKey={yKey} fill={colorSingle} radius={[3, 3, 0, 0]}
          {...(showLabel ? { label: { position: "top", fontSize: 10, fill: "#334155" } } : {})}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const SUB_TABLE_CONFIG = {
  design:  { label: "Design",  icon: "🎯", canCreate: true,  needsSmiles: true,  needsId: true  },
  synthesis:{ label: "Synthesis",icon: "⚗️", canCreate: true, needsSmiles: false, needsId: true  },
  activity: { label: "Activity", icon: "🔬", canCreate: false, needsSmiles: false, needsId: false  },
  dmpk:     { label: "DMPK",    icon: "📈", canCreate: false, needsSmiles: false, needsId: true  },
  asset:    { label: "Assets",  icon: "📦", canCreate: false, needsSmiles: false, needsId: true  },
  evolution:{ label: "Evolution",icon:"🔗", canCreate: false, needsSmiles: false, needsId: true  },
  other:    { label: "Other",   icon: "📋", canCreate: false, needsSmiles: false, needsId: true  },
};

const SubTableView = observer(({ tableType, projectId }) => {
  const toast = useRef(null);
  const fileInputRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(50);
  const [displayStructurePreview, setDisplayStructurePreview] = useState(false);
  const [structurePreviewSmiles, setStructurePreviewSmiles] = useState(null);
  const [tableScrollHeight, setTableScrollHeight] = useState("flex");

  // ── Ketcher molecule editor state ──
  const [ketcherVisible, setKetcherVisible] = useState(false);
  const [ketcherSmiles, setKetcherSmiles] = useState("");
  const [editingCompoundRow, setEditingCompoundRow] = useState(null);
  // Confirm dialog after Ketcher edit
  const [confirmEditVisible, setConfirmEditVisible] = useState(false);
  const [newSmilesFromKetcher, setNewSmilesFromKetcher] = useState("");

  const cfg = SUB_TABLE_CONFIG[tableType] || { label: tableType, icon: "📋", canCreate: false, needsSmiles: false, needsId: true };

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Column management
  const [showColumnDialog, setShowColumnDialog] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [newColumnDisplay, setNewColumnDisplay] = useState("");

  // Add Row dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addId, setAddId] = useState("");
  const [addSmiles, setAddSmiles] = useState("");
  const [addExtra, setAddExtra] = useState("{}");
  const [saving, setSaving] = useState(false);
  // Available DesignIds for batch-add (synthesis mode)
  const [availableDesignIds, setAvailableDesignIds] = useState([]);
  const [addSelectedDesignIds, setAddSelectedDesignIds] = useState([]);

  // ── Activity Dialog ──
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [editingActivityRow, setEditingActivityRow] = useState(null); // null = add new
  const [activitySaving, setActivitySaving] = useState(false);
  const [availableEntities, setAvailableEntities] = useState([]);
  const [activityForm, setActivityForm] = useState({
    entityId: "", compoundId: "", assayName: "", assayType: "",
    value: "", unit: "", method: "", condition: "",
    targetName: "", pmid: "", notes: "",
  });

  // Inline editing
  const [editingRow, setEditingRow] = useState(null);
  // Local state for inline edit values (fixes controlled input issue)
  const [editValues, setEditValues] = useState({});

  // ── Data Fetching ──
  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [data, cols] = await Promise.all([
        agent.Compounds.subTable.list(tableType, projectId),
        agent.Compounds.subTable.listColumns(tableType, projectId),
      ]);
      const parsed = (data || []).map((r) => ({
        ...r,
        extra: r.extraData ? JSON.parse(r.extraData) : {},
      }));
      setRows(parsed);
      setColumns(cols || []);
      // Compute pivot data for activity table, using column definitions if available
      if (tableType === "activity") {
        let colDefs = [];
        try {
          colDefs = await agent.Compounds.subTable.assayColumns.list(projectId);
          setAssayColumns(colDefs || []);
        } catch {}
        computePivotData(parsed, colDefs || []);
      }
    } catch (err) {
      console.error(`[SubTableView] Failed to load ${cfg.label}:`, JSON.parse(JSON.stringify(err, Object.getOwnPropertyNames(err))));
      console.error(`[SubTableView] err keys:`, Object.keys(err));
      console.error(`[SubTableView] err.message:`, err?.message);
      console.error(`[SubTableView] err.status:`, err?.status);
      console.error(`[SubTableView] err.data:`, err?.data);
      // Try to extract a useful message
      let msg = "Unknown error";
      if (err?.data?.title) msg = err.data.title;
      else if (err?.message) msg = err.message;
      else if (typeof err === "string") msg = err;
      else if (err?.status) msg = `HTTP ${err.status}`;
      if (msg === "Unknown error") {
        try { msg = JSON.stringify(err).slice(0, 200); } catch(_) {}
      }
      if (err?.status === 401) return;
      toast.current?.show({ severity: "error", summary: `Failed to load ${cfg.label}`, detail: msg, life: 6000 });
    } finally {
      setLoading(false);
    }
  }, [projectId, tableType, agent, cfg.label]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Calculate available height for table ──
  useEffect(() => {
    const updateHeight = () => {
      if (tableWrapperRef.current) {
        const rect = tableWrapperRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top - 60; // 60px for paginator + margin
        // Only set fixed height if the calculation gives a reasonable value
        if (availableHeight > 200) {
          setTableScrollHeight(`${availableHeight}px`);
        }
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    // Recalculate after render settling
    const timer = setTimeout(updateHeight, 300);
    return () => {
      window.removeEventListener("resize", updateHeight);
      clearTimeout(timer);
    };
  }, [rows]);

  // ── SmilesDrawer canvas auto-sizing ──
  const redrawAllCanvases = useCallback(() => {
    document.querySelectorAll(".subtable-structure-canvas").forEach((el) => {
      const smiles = el.dataset.smiles;
      if (!smiles) return;
      const parent = el.parentElement;
      const cellWidth = parent?.offsetWidth || 200;
      const cellHeight = parent?.offsetHeight || 60;
      const w = Math.max(cellWidth - 16, 60);
      const h = Math.max(cellHeight - 4, 36);
      const dpr = window.devicePixelRatio || 1;
      el.width = w * dpr;
      el.height = h * dpr;
      el.style.width = w + "px";
      el.style.height = h + "px";
      try {
        const drawer = new SmilesDrawer.Drawer({
          width: w * dpr, height: h * dpr,
          bondThickness: 1.0, shortBondLength: 1.0,
          fontSizeScale: 0.8, atomVisualization: "default",
          isomericHighlight: true, explicitHydrogens: false,
        });
        SmilesDrawer.parse(
          smiles,
          (tree) => drawer.draw(tree, el.id, "light", false),
          (err) => console.error("SmilesDrawer error:", err)
        );
      } catch (e) {
        console.error("SmilesDrawer render error:", e);
      }
    });
  }, []);

  useEffect(() => {
    if (rows.length > 0) {
      const t1 = setTimeout(() => redrawAllCanvases(), 100);
      const t2 = setTimeout(() => redrawAllCanvases(), 500);
      const t3 = setTimeout(() => redrawAllCanvases(), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [rows, rowHeight, redrawAllCanvases]);

  // ── Column Management ──
  const handleAddColumn = async () => {
    if (!newColumnName.trim() || !newColumnDisplay.trim()) return;
    const updated = [...columns, {
      columnName: newColumnName.trim(),
      displayName: newColumnDisplay.trim(),
      dataType: "string",
      sortOrder: columns.length,
    }];
    try {
      await agent.Compounds.subTable.saveColumns(tableType, projectId, { columns: updated });
      setColumns(updated);
      setNewColumnName(""); setNewColumnDisplay(""); setShowColumnDialog(false);
      toast.current.show({ severity: "success", summary: "Column added", life: 2000 });
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to add column", life: 3000 });
    }
  };

  const handleRemoveColumn = async (colName) => {
    const updated = columns.filter((c) => c.columnName !== colName);
    try {
      await agent.Compounds.subTable.saveColumns(tableType, projectId, { columns: updated });
      setColumns(updated);
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to remove column", life: 3000 });
    }
  };

  // ── Add Row ──
  const handleAddRow = async () => {
    // Activity uses separate Manage Columns + Edit Values flow
    if (tableType === "activity") return;
    const hasBatchSelection = tableType === "synthesis" && addSelectedDesignIds.length > 0;
    if (cfg.needsId && !addId.trim() && !hasBatchSelection) {
      toast.current.show({ severity: "warn", summary: "Compound ID is required", life: 2000 });
      return;
    }
    if (cfg.needsSmiles && !addSmiles.trim()) {
      toast.current.show({ severity: "warn", summary: "SMILES is required for Design entries", life: 2000 });
      return;
    }

    setSaving(true);
    try {
      let parsedExtra = {};
      try { parsedExtra = JSON.parse(addExtra); } catch { parsedExtra = {}; }

      // Collect all DesignIds to add: from batch-select + manual input
      const designIdsToAdd = [];
      if (tableType === "synthesis" && addSelectedDesignIds.length > 0) {
        designIdsToAdd.push(...addSelectedDesignIds);
      }
      if (addId.trim()) {
        designIdsToAdd.push(addId.trim());
      }

      if (designIdsToAdd.length === 0 && cfg.needsId) {
        toast.current.show({ severity: "warn", summary: "Please select or enter a compound ID", life: 2000 });
        setSaving(false);
        return;
      }

      if (designIdsToAdd.length > 0) {
        // Batch-add each DesignId
        for (const did of designIdsToAdd) {
          const payload = {
            projectId,
            compoundId: null,
            extraData: JSON.stringify(parsedExtra),
            smile: null,
            externalCompoundIds: did,
            designId: did,
          };
          await agent.Compounds.subTable.save(tableType, payload);
        }
      } else {
        // Standard single-add for non-synthesis types
        const payload = {
          projectId,
          compoundId: null,
          extraData: JSON.stringify(parsedExtra),
          smile: cfg.needsSmiles ? addSmiles.trim() : null,
          externalCompoundIds: cfg.needsId ? addId.trim() : null,
          designId: cfg.needsId ? addId.trim() : null,
        };
        await agent.Compounds.subTable.save(tableType, payload);
      }

      toast.current.show({ severity: "success", summary: designIdsToAdd.length > 1 ? `${designIdsToAdd.length} rows added` : "Row added", life: 2000 });
      setShowAddDialog(false);
      setAddId(""); setAddSmiles(""); setAddExtra("{}");
      setAddSelectedDesignIds([]);
      setAvailableDesignIds([]);
      fetchData();
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Failed to add row", life: 3000 });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete Row ──
  const handleDelete = async (row) => {
    // Activity records are managed through separate API
    if (tableType === "activity") return;
    if (!window.confirm(`Delete this ${cfg.label} record?`)) return;
    try {
      await agent.Compounds.subTable.delete(tableType, row.id);
      toast.current.show({ severity: "success", summary: "Deleted", life: 2000 });
      fetchData();
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to delete", life: 3000 });
    }
  };

  // ── Inline ExtraData Edit ──
  const handleExtraChange = async (row, colName, value) => {
    // Activity uses a separate Manage Columns + Edit Values flow
    if (tableType === "activity") return;
    const updated = { ...row.extra, [colName]: value };
    const payload = {
      projectId,
      compoundId: row.compoundId,
      extraData: JSON.stringify(updated),
      id: row.id,   // Pass sub-table record ID so backend updates it instead of creating new
    };
    try {
      await agent.Compounds.subTable.save(tableType, { ...payload, smile: null, externalCompoundIds: null });
      // Refresh to get clean state
      fetchData();
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to update", life: 3000 });
    }
  };

  // ── Activity: Pivot Table ──
  const [pivotRows, setPivotRows] = useState([]);
  const [pivotColumns, setPivotColumns] = useState([]);

  // Transform flat activity records into pivot table (rows=EntityId, cols=column definitions)
  const computePivotData = useCallback((rawData, columns) => {
    const items = rawData || [];
    const colDefs = columns || [];
    // Group by entityId
    const groups = {};
    items.forEach(r => {
      const key = r.entityId || `unknown-${r.id}`;
      if (!groups[key]) groups[key] = {
        _entityId: key, _designId: r.designId, _compoundId: r.compoundId,
        _smile: r.smile || r.canonicalSmile || "",
        _canonicalSmile: r.canonicalSmile || r.smile || "",
        _records: []
      };
      groups[key]._records.push(r);
    });
    // Column headers: use column definitions if available, otherwise fall back to data-driven
    let colArray;
    if (colDefs.length > 0) {
      colArray = colDefs.map(c => c.assayName).filter(Boolean);
    } else {
      const colSet = new Set();
      items.forEach(r => { if (r.assayName) colSet.add(r.assayName); });
      colArray = Array.from(colSet).sort();
    }
    // Build pivot rows
    const pivot = Object.values(groups).map(g => {
      const row = {
        entityId: g._entityId,
        designId: g._designId,
        compoundId: g._compoundId,
        smile: g._smile,
        canonicalSmile: g._canonicalSmile,
        _records: g._records,
      };
      g._records.forEach(r => {
        const colKey = r.assayName;
        if (colKey) row[colKey] = r.value || "—";
      });
      return row;
    });
    setPivotRows(pivot);
    setPivotColumns(colArray);
  }, []);

  // ── Activity: Manage Columns ──
  const openManageColumns = () => {
    setShowManageColumns(true);
    setColumnOrderChanged(false);
    setShowNewColumnSection(false);
    setColumnSaving(true);
    agent.Compounds.subTable.assayColumns.list(projectId)
      .then(cols => { setAssayColumns(cols || []); })
      .finally(() => setColumnSaving(false));
    setNewColumnForm({ method: "", targetName: "", assayType: "", unit: "", condition: "", pmid: "", note: "" });
  };

  const handleNewColumnFieldChange = (field, value) => {
    setNewColumnForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-generate assay name
      const method = next.method?.trim();
      const target = next.targetName?.trim();
      const atype = next.assayType?.trim();
      const unit = next.unit?.trim();
      const parts = [];
      if (method) parts.push(method);
      if (target) parts.push(`(${target})`);
      // Abbreviate assay type
      const abbrev = atype ? atype.split(/[-\/]/).filter(Boolean).pop() || atype : "";
      if (abbrev) parts.push(`-${abbrev}`);
      if (unit) parts.push(`(${unit})`);
      next._assayName = parts.join("");
      return next;
    });
  };

  const handleAddAssayColumn = async () => {
    const f = newColumnForm;
    const assayName = f._assayName || "";
    if (!assayName) { toast.current.show({ severity: "warn", summary: "Cannot generate AssayName, fill Method/AssayType", life: 2000 }); return; }
    setColumnSaving(true);
    try {
      await agent.Compounds.subTable.assayColumns.create({
        projectId,
        assayName,
        assayType: f.assayType,
        unit: f.unit,
        method: f.method,
        targetName: f.targetName,
        condition: f.condition,
        pmid: f.pmid,
        notes: f.note,
      });
      toast.current.show({ severity: "success", summary: "Column added", life: 2000 });
      setNewColumnForm({ method: "", targetName: "", assayType: "", unit: "", condition: "", pmid: "", note: "" });
      // Refresh columns
      const cols = await agent.Compounds.subTable.assayColumns.list(projectId);
      setAssayColumns(cols || []);
      fetchData(); // Refresh pivot table
    } catch (err) {
      const msg = err?.data || err?.response?.data?.title || err?.message || "Failed";
      toast.current.show({ severity: "error", summary: "Failed to add column", detail: msg, life: 3000 });
    } finally {
      setColumnSaving(false);
    }
  };

  // ── Activity: Bar Chart Compare ──
  const [chartVisible, setChartVisible] = useState(false);
  const [chartConfig, setChartConfig] = useState({
    height: 300, barWidth: 24, barGap: 4, legendPos: "bottom",
    showGrid: true, showTooltip: true, showLegend: true, showLabel: true,
  });
  const [chartColumns, setChartColumns] = useState([]); // ['IC50(nM)', 'CC50(uM)', ...]
  const [chartMolecules, setChartMolecules] = useState([]); // ['ENT-001', 'ENT-002', ...]

  const toggleChartColumn = (colName) => {
    setChartColumns(prev =>
      prev.includes(colName) ? prev.filter(c => c !== colName) : [...prev, colName]
    );
  };

  const toggleChartMolecule = (entityId) => {
    setChartMolecules(prev =>
      prev.includes(entityId) ? prev.filter(e => e !== entityId) : [...prev, entityId]
    );
  };

  const openChartDialog = () => {
    // Default: select first column and all molecules with data
    if (chartColumns.length === 0 && pivotColumns.length > 0) {
      setChartColumns([pivotColumns[0]]);
    }
    if (chartMolecules.length === 0 && pivotRows.length > 0) {
      setChartMolecules(pivotRows.filter(r => r.entityId).map(r => r.entityId));
    }
    setChartVisible(true);
  };

  // Build chart data from pivotRows
  const chartData = React.useMemo(() => {
    if (!chartVisible || chartColumns.length === 0) return [];
    const result = [];
    const molecules = chartMolecules.length > 0
      ? pivotRows.filter(r => r.entityId && chartMolecules.includes(r.entityId))
      : pivotRows.filter(r => r.entityId);
    molecules.forEach(row => {
      chartColumns.forEach(col => {
        const val = parseFloat(row[col]);
        if (!isNaN(val)) {
          result.push({ entityId: row.entityId, assayName: col, value: val });
        }
      });
    });
    return result;
  }, [chartVisible, chartColumns, chartMolecules, pivotRows]);

  const handleDeleteColumn = async (colId) => {
    if (!window.confirm("Delete this column? Related activity values will be hidden.")) return;
    try {
      await agent.Compounds.subTable.assayColumns.delete(colId);
      toast.current.show({ severity: "success", summary: "Column deleted", life: 2000 });
      const cols = await agent.Compounds.subTable.assayColumns.list(projectId);
      setAssayColumns(cols || []);
      fetchData();
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to delete column", life: 3000 });
    }
  };

  // Drag-and-drop reorder
  const [dragColId, setDragColId] = useState(null);

  const handleDragStart = (colId) => { setDragColId(colId); };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    if (!dragColId || dragColId === colId) return;
    // Reorder in local state
    setAssayColumns(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c.id === dragColId);
      const toIdx = arr.findIndex(c => c.id === colId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setDragColId(colId); // update to prevent re-triggering for same target
  };

  const handleDrop = () => {
    setDragColId(null);
    setColumnOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    const orderedIds = assayColumns.map(c => c.id);
    try {
      await agent.Compounds.subTable.assayColumns.reorder({ projectId, columnIds: orderedIds });
      setColumnOrderChanged(false);
      toast.current.show({ severity: "success", summary: "Column order saved", life: 2000 });
      fetchData();
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to save column order", life: 3000 });
    }
  };

  const [showActivitySubList, setShowActivitySubList] = useState(false);
  const [activitySubRecords, setActivitySubRecords] = useState([]);
  const [subListEntityLabel, setSubListEntityLabel] = useState("");

  // Activity: Manage Columns dialog
  const [showManageColumns, setShowManageColumns] = useState(false);
  const [assayColumns, setAssayColumns] = useState([]);
  const [newColumnForm, setNewColumnForm] = useState({
    method: "", targetName: "", assayType: "", unit: "", condition: "", pmid: "", note: "",
  });
  const [columnSaving, setColumnSaving] = useState(false);
  const [columnOrderChanged, setColumnOrderChanged] = useState(false);
  const [showNewColumnSection, setShowNewColumnSection] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null); // column object being edited, null = not editing

  const handleEditColumnOpen = (col) => {
    setEditingColumn({
      ...col,
      _method: col.method || "",
      _assayType: col.assayType || "",
      _targetName: col.targetName || "",
      _unit: col.unit || "",
      _condition: col.condition || "",
      _pmid: col.pmid || "",
      _note: col.notes || "",
    });
  };

  const handleEditColumnFieldChange = (field, value) => {
    setEditingColumn(prev => {
      const next = { ...prev, [field]: value };
      // Auto-generate assay name preview
      const method = next._method?.trim();
      const target = next._targetName?.trim();
      const atype = next._assayType?.trim();
      const unit = next._unit?.trim();
      const parts = [];
      if (method) parts.push(method);
      if (target) parts.push(`(${target})`);
      const abbrev = atype ? atype.split(/[-\/]/).filter(Boolean).pop() || atype : "";
      if (abbrev) parts.push(`-${abbrev}`);
      if (unit) parts.push(`(${unit})`);
      next._assayNamePreview = parts.join("");
      return next;
    });
  };

  const handleEditColumnSave = async () => {
    const col = editingColumn;
    if (!col?.id) return;
    setColumnSaving(true);
    try {
      await agent.Compounds.subTable.assayColumns.update({
        id: col.id,
        assayType: col._assayType,
        unit: col._unit,
        method: col._method,
        targetName: col._targetName,
        condition: col._condition,
        pmid: col._pmid,
        notes: col._note,
      });
      toast.current.show({ severity: "success", summary: "Column updated", life: 2000 });
      setEditingColumn(null);
      // Refresh columns
      const cols = await agent.Compounds.subTable.assayColumns.list(projectId);
      setAssayColumns(cols || []);
      fetchData();
    } catch (err) {
      const msg = err?.data || err?.response?.data?.title || err?.message || "Failed";
      toast.current.show({ severity: "error", summary: "Failed to update column", detail: msg, life: 3000 });
    } finally {
      setColumnSaving(false);
    }
  };

  // Activity: Inline cell editing values
  const [activityCellValues, setActivityCellValues] = useState({}); // { assayName: value, ... }

  const openActivityDialog = (pivotRow = null, record = null) => {
    if (pivotRow && pivotRow._records) {
      // Pivot row clicked: show sub-list of records for this entity
      setActivitySubRecords(pivotRow._records);
      setSubListEntityLabel(pivotRow.entityId || pivotRow.designId || "");
      setShowActivitySubList(true);
      return;
    }
    if (record) {
      // Edit mode for a specific record
      setEditingActivityRow(record);
      setActivityForm({
        entityId: record.entityId || "",
        compoundId: record.compoundId || "",
        assayName: record.assayName || "",
        assayType: record.assayType || "",
        value: record.value || "",
        unit: record.unit || "",
        method: record.method || "",
        condition: record.condition || "",
        targetName: record.targetName || "",
        pmid: record.pmid || "",
        notes: record.notes || "",
      });
      setShowActivityDialog(true);
    } else {
      // Add mode
      setEditingActivityRow(null);
      setActivityForm({
        entityId: "", compoundId: "", assayName: "", assayType: "",
        value: "", unit: "", method: "", condition: "",
        targetName: "", pmid: "", notes: "",
      });
      agent.Compounds.subTable.listDesignIds(projectId)
        .then(list => setAvailableEntities(Array.isArray(list) ? list.map(d => ({ entityId: d, designId: d })) : []))
        .catch(() => {});
      setShowActivityDialog(true);
    }
  };

  const handleActivityFormChange = (field, value) => {
    setActivityForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-generate AssayName when Method/TargetName/AssayType/Unit change
      if (["method", "targetName", "assayType", "unit"].includes(field)) {
        next.assayName = generateAssayName(next);
      }
      return next;
    });
  };

  // Helper: abbreviate AssayType (take last segment after "-" or "/")
  const abbreviateAssayType = (type) => {
    if (!type) return "";
    const parts = type.split(/[-\/]/).filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : type;
  };

  // Helper: auto-generate AssayName from Method/TargetName/AssayType/Unit
  const generateAssayName = (f) => {
    const parts = [];
    // Method prefix
    if (f.method?.trim()) parts.push(f.method.trim());
    // TargetName in parentheses (omit if empty)
    const target = f.targetName?.trim();
    const methodAndTarget = parts.join("");
    const targetPart = target ? `(${target})` : "";
    // AssayType abbreviation
    const assayAbbr = abbreviateAssayType(f.assayType?.trim());
    const assayPart = assayAbbr ? `-${assayAbbr}` : "";
    // Unit in parentheses (omit if empty)
    const unit = f.unit?.trim();
    const unitPart = unit ? `(${unit})` : "";
    return `${methodAndTarget}${targetPart}${assayPart}${unitPart}`;
  };

  const handleActivitySave = async () => {
    const f = activityForm;
    // Validate required fields
    if (!f.assayName.trim()) { toast.current.show({ severity: "warn", summary: "Assay Name is required", life: 2000 }); return; }
    if (!f.assayType.trim()) { toast.current.show({ severity: "warn", summary: "Assay Type is required", life: 2000 }); return; }
    if (!f.value.trim()) { toast.current.show({ severity: "warn", summary: "Value is required", life: 2000 }); return; }
    if (!f.unit.trim()) { toast.current.show({ severity: "warn", summary: "Unit is required", life: 2000 }); return; }
    if (!f.method.trim()) { toast.current.show({ severity: "warn", summary: "Method is required", life: 2000 }); return; }

    setActivitySaving(true);
    try {
      if (editingActivityRow) {
        // Update existing
        await agent.Compounds.editActivity(editingActivityRow.id, {
          assayName: f.assayName, assayType: f.assayType,
          value: f.value, unit: f.unit, method: f.method,
          condition: f.condition, targetName: f.targetName,
          pmid: f.pmid, notes: f.notes,
        });
        toast.current.show({ severity: "success", summary: "Activity updated", life: 2000 });
      } else {
        // Create new — need compoundId
        if (!f.compoundId) { toast.current.show({ severity: "warn", summary: "Please select a compound", life: 2000 }); setActivitySaving(false); return; }
        await agent.Compounds.addActivity(f.compoundId, {
          assayName: f.assayName, assayType: f.assayType,
          value: f.value, unit: f.unit, method: f.method,
          condition: f.condition, targetName: f.targetName,
          pmid: f.pmid, notes: f.notes,
        });
        toast.current.show({ severity: "success", summary: "Activity added", life: 2000 });
      }
      setShowActivityDialog(false);
      setEditingActivityRow(null);
      fetchData();
    } catch (err) {
      const msg = err?.data || err?.response?.data?.title || err?.message || "Save failed";
      toast.current.show({ severity: "error", summary: "Failed", detail: msg, life: 4000 });
    } finally {
      setActivitySaving(false);
    }
  };

  // ── CSV Import ──
  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await agent.Compounds.subTable.importCsv(tableType, projectId, file);
      const count = typeof result === "number" ? result : result?.value ?? result;
      toast.current.show({ severity: "success", summary: `Imported ${count} rows`, life: 3000 });
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.title || err?.message || "Import failed";
      toast.current.show({ severity: "error", summary: "Import failed", detail: msg, life: 4000 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Row Edit State ──
  const [activeEditRowId, setActiveEditRowId] = useState(null);

  // ── Ketcher editor handlers (Design only) ──
  const openKetcher = (row) => {
    const currentSmiles = row.canonicalSmile || row.smile || "";
    setKetcherSmiles(currentSmiles);
    setEditingCompoundRow(row);
    setKetcherVisible(true);
  };

  const handleKetcherConfirm = (loadedSmiles, newSmiles) => {
    setKetcherVisible(false);
    // loadedSmiles is the canonicalized version from Ketcher after loading the initial SMILES
    console.log(`[Ketcher] loaded: "${loadedSmiles}" vs new: "${newSmiles}"`);
    if (!newSmiles || newSmiles === loadedSmiles) {
      if (!newSmiles) {
        toast.current?.show({ severity: "warn", summary: "Empty SMILES, ignored", life: 1500 });
      } else {
        toast.current?.show({ severity: "info", summary: "No changes detected", life: 1500 });
      }
      return;
    }
    setNewSmilesFromKetcher(newSmiles);
    setConfirmEditVisible(true);
  };

  const handleEditConfirm = async (mode) => {
    // mode: "create" = create new molecule, "overwrite" = update existing
    setConfirmEditVisible(false);
    const row = editingCompoundRow;
    if (!row) return;

    const payload = {
      projectId,
      compoundId: mode === "overwrite" ? row.compoundId : null,
      extraData: row.extraData || null,
      smile: newSmilesFromKetcher,
      externalCompoundIds: mode === "create" ? null : row.externalCompoundIds,
      designId: mode === "create" ? null : row.designId,
      overwriteMode: mode === "overwrite",
    };

    try {
      await agent.Compounds.subTable.save(tableType, payload);
      toast.current.show({
        severity: "success",
        summary: mode === "create" ? "New molecule created" : "SMILES updated",
        life: 2000,
      });
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.title || err?.message || "Failed to save";
      toast.current.show({ severity: "error", summary: "Save failed", detail: msg, life: 4000 });
    }
  };

  // ── Actions Column Template ──
  const actionsTemplate = (row) => {
    // Activity pivot rows don't have `id`, use entityId for edit state
    const editKey = tableType === "activity" ? (row.entityId || row.id) : row.id;
    const isEditing = activeEditRowId === editKey;
    // Activity uses inline editing in pivot table
    if (tableType === "activity") {
      return (
        <div className="flex gap-1 subtable-actions" style={{ justifyContent: "center" }}>
          {isEditing ? (
            <>
              <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
                tooltip="Save changes" onClick={() => { setActiveEditRowId(null); setActivityCellValues({}); fetchData(); }} />
              <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Delete activity records for this compound"
                onClick={async () => {
                  if (!window.confirm("Delete all activity records for this compound?")) return;
                  const ids = (row._records || []).map(r => r.id).filter(Boolean);
                  for (const id of ids) {
                    try { await agent.Compounds.deleteActivity(id); } catch {}
                  }
                  toast.current.show({ severity: "success", summary: "Deleted", life: 2000 });
                  fetchData();
                }} />
              <Button icon="pi pi-times" className="p-button-rounded p-button-secondary p-button-text p-button-sm"
                tooltip="Cancel" onClick={() => { setActiveEditRowId(null); setActivityCellValues({}); }} />
            </>
          ) : (
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-info p-button-text p-button-sm"
              tooltip="Edit values for this compound"
              onClick={() => {
                setActiveEditRowId(row.entityId || row.id);
                const vals = {};
                (row._records || []).forEach(r => { if (r.assayName) vals[r.assayName] = r.value || ""; });
                setActivityCellValues(vals);
              }} />
          )}
        </div>
      );
    }
    return (
      <div className="flex gap-1 subtable-actions" style={{ justifyContent: "center" }}>
        {isEditing ? (
          <>
            <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
              tooltip="Save changes" onClick={() => { setActiveEditRowId(null); setEditValues({}); fetchData(); }} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
              tooltip="Delete this record" onClick={() => handleDelete(row)} />
            <Button icon="pi pi-times" className="p-button-rounded p-button-secondary p-button-text p-button-sm"
              tooltip="Cancel" onClick={() => { setActiveEditRowId(null); setEditValues({}); }} />
          </>
        ) : (
          <div className="flex gap-1">
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-info p-button-text p-button-sm"
              tooltip="Edit"
              onClick={() => {
                // Initialize edit values for this row
                setEditValues({
                  [`syn-${row.id}`]: row.synthesisId || "",
                  [`ent-${row.id}`]: row.entityId || "",
                });
                setActiveEditRowId(row.id);
              }} />
            {tableType === "design" && (
              <Button icon="pi pi-arrow-right" className="p-button-rounded p-button-success p-button-text p-button-sm"
                tooltip="Schedule for Synthesis"
                onClick={async () => {
                  try {
                    await agent.Compounds.subTable.save("synthesis", { compoundId: row.compoundId, projectId });
                    toast.current.show({ severity: "success", summary: "Scheduled for Synthesis", life: 2000 });
                    fetchData();
                  } catch (err) {
                    const msg = err?.data || err?.response?.data?.title || err?.message || "";
                    if (msg.includes("already been scheduled")) {
                      toast.current.show({ severity: "warn", summary: "Already scheduled", detail: "This compound was already pushed to Synthesis", life: 3000 });
                    } else {
                      toast.current.show({ severity: "error", summary: "Failed", detail: err?.response?.data?.title || err?.message, life: 4000 });
                    }
                  }
                }} />
            )}
          </div>
        )}
      </div>
    );
  };

  // ── ExtraData Inline Edit Template ──
  const extraTemplate = (col) => (row) => {
    const val = row.extra ? row.extra[col.columnName] : "";
    const isEditing = (activeEditRowId === row.id) || (editingRow?.id === row.id && editingRow?.col === col.columnName);
    if (isEditing) {
      return (
        <InputText
          value={val || ""}
          autoFocus={editingRow?.col === col.columnName}
          style={{ width: "100%", fontSize: "0.8rem", padding: "2px 6px" }}
          onChange={(e) => {
            // Update local state immediately for visual feedback
            const updated = { ...row.extra, [col.columnName]: e.target.value };
            row.extra = updated;
          }}
          onBlur={(e) => {
            setEditingRow(null);
            handleExtraChange(row, col.columnName, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.target.blur(); }
            if (e.key === "Escape") { setEditingRow(null); setActiveEditRowId(null); }
          }}
        />
      );
    }
    return (
      <span
        style={{ cursor: "pointer", minHeight: 24, display: "inline-block", width: "100%" }}
        onClick={() => setEditingRow({ id: row.id, col: col.columnName })}
        title="Click to edit"
      >
        {val != null ? String(val) : ""}
      </span>
    );
  };

  // ── Breadcrumb items ──
  const breadcrumbHome = { icon: "pi pi-home", url: "#" };

  // ── Breadcrumb items ──
  const breadcrumbItems = [
    { label: "Compounds" },
    { label: cfg.label },
  ];

  return (
    <div className="flex flex-column w-full subtable-wrapper" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Toast ref={toast} />

      {/* ── Breadcrumb ── */}
      <div className="flex w-full pb-2">
        <BreadCrumb model={breadcrumbItems} />
      </div>

      {/* ── Section Heading ── */}
      <SectionHeading
        icon="icon icon-common icon-math"
        heading={cfg.label}
        color="#1e1b4b"
        textColor="#ffffff"
        customButtons={[
          { label: "Add Row", icon: "pi pi-plus", action: () => {
            if (tableType === "activity") {
              openActivityDialog(null);
            } else {
              setShowAddDialog(true);
              // Fetch available DesignIds for synthesis batch-add
              if (tableType === "synthesis") {
                agent.Compounds.subTable.listDesignIds(projectId)
                  .then(ds => setAvailableDesignIds(ds || []))
                  .catch(() => {});
              }
            }
          }, className: "p-button-sm subtable-toolbar-btn" },
          { label: "Manage Columns", icon: "pi pi-cog", action: () => {
            if (tableType === "activity") {
              openManageColumns();
            } else {
              setShowColumnDialog(true);
            }
          }, className: "p-button-sm subtable-toolbar-btn" },
          { label: "Import CSV", icon: "pi pi-upload", action: () => fileInputRef.current?.click(), className: "p-button-sm subtable-toolbar-btn" },
          ...(tableType === "activity" ? [{
            label: "Download Template", icon: "pi pi-download", action: () => {
              const csv = "EntityId,AssayName,AssayType,Value,Unit,Method,Condition,TargetName,PMID,Notes\nENT-001,IC50,Primary,12.5,nM,ELISA,10uM compound,KRAS,12345678,\nENT-002,CC50,Toxicity,50,uM,MTT,72h,,,,\n";
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = "activity_template.csv";
              link.click();
              URL.revokeObjectURL(link.href);
            }, className: "p-button-sm p-button-outlined p-button-secondary subtable-toolbar-btn",
          }] : []),
          ...(tableType === "activity" ? [{
            label: "📊 Chart", icon: "pi pi-chart-bar", action: () => openChartDialog(),
            className: "p-button-sm subtable-toolbar-btn",
          }] : []),
        ]}
      />
      <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleImportCsv} />

      {/* ── Data Table (fills remaining height) ── */}
      <div ref={tableWrapperRef} className="flex w-full" style={{ flex: 1, minHeight: 0 }}>
        <DataTable
          value={tableType === "activity" ? pivotRows : rows}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          className="w-full"
          scrollHeight={tableScrollHeight}
          emptyMessage={
            <div className="subtable-empty-state">
              <i className="pi pi-inbox" />
              <span>No {cfg.label} records yet. Add one or import CSV.</span>
            </div>
          }
          sortField={tableType === "design" ? "designId" : "createdAt"}
          sortOrder={1}
          size="small"
          scrollable
          stripedRows
          resizableColumns
          columnResizeMode="expand"
        >
        {/* Compound ID — varies by table type */}
        {tableType === "design" && (
          <Column field="designId" header="Design ID" sortable style={{ minWidth: 100, width: 100, maxWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => (
              <span className="subtable-id-cell">{row.designId || row.externalCompoundIds || row.entityId || "—"}</span>
            )}
          />
        )}
        {tableType === "synthesis" && [
          <Column key="designId" field="designId" header="Design ID" sortable style={{ minWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => (
              <span style={{ fontSize: "13px", fontWeight: 500, color: "#334155" }}>{row.designId || row.externalCompoundIds || "—"}</span>
            )}
          />,
          <Column key="synthesisId" field="synthesisId" header="Synthesis ID" sortable style={{ minWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row, options) => {
              const isEditing = activeEditRowId === row.id;
              const editKey = `syn-${row.id}`;
              const sid = editKey in editValues ? editValues[editKey] : (row.synthesisId || "");
              if (isEditing) {
                return (
                  <InputText
                    value={sid}
                    onChange={(e) => { setEditValues(prev => ({ ...prev, [editKey]: e.target.value })); }}
                    onBlur={async () => {
                      const val = editValues[editKey];
                      if (row.compoundId && val !== undefined) {
                        try {
                          await agent.Compounds.edit(row.compoundId, { synthesisId: val });
                          // Update row data safely via functional updater
                          setRows(prev => prev.map(r => r.id === row.id ? { ...r, synthesisId: val } : r));
                          setEditValues(prev => { const n={...prev}; delete n[editKey]; return n; });
                          toast.current?.show?.({ severity: "success", summary: "Saved", life: 2000 });
                        } catch { /* ignore */ }
                      }
                    }}
                    style={{ width: "100%", fontSize: "12px" }}
                  />
                );
              }
              return <span className="subtable-id-cell">{sid || "—"}</span>;
            }}
          />,
          <Column key="entityId" field="entityId" header="Entity ID" sortable style={{ minWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row, options) => {
              const isEditing = activeEditRowId === row.id;
              const editKey = `ent-${row.id}`;
              const eid = editKey in editValues ? editValues[editKey] : (row.entityId || "");
              const statusOk = row.extra?.status === "success";
              if (isEditing && statusOk) {
                return (
                  <InputText
                    value={eid}
                    onChange={(e) => { setEditValues(prev => ({ ...prev, [editKey]: e.target.value })); }}
                    onBlur={async () => {
                      const val = editValues[editKey];
                      if (row.compoundId && val !== undefined) {
                        try {
                          await agent.Compounds.edit(row.compoundId, { entityId: val });
                          // Update row data safely via functional updater
                          setRows(prev => prev.map(r => r.id === row.id ? { ...r, entityId: val } : r));
                          setEditValues(prev => { const n={...prev}; delete n[editKey]; return n; });
                          toast.current?.show?.({ severity: "success", summary: "Saved", life: 2000 });
                        } catch { /* ignore */ }
                      }
                    }}
                    style={{ width: "100%", fontSize: "12px" }}
                  />
                );
              }
              if (isEditing && !statusOk) {
                return (
                  <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic", cursor: "not-allowed" }}>
                    <i className="pi pi-lock" style={{ marginRight: 4, fontSize: "10px" }} />
                    Set status to Success first
                  </span>
                );
              }
              return (
                <span style={{ fontSize: "13px", fontWeight: 500, color: eid ? "#6366f1" : "#94a3b8" }}>
                  {eid || "—"}
                </span>
              );
            }}
          />,
          <Column key="status" header="Status" sortable style={{ minWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => {
              const status = row.extra?.status || "pending";
              const isEditing = activeEditRowId === row.id;
              if (isEditing) {
                return (
                  <Dropdown
                    value={status}
                    options={[
                      { label: "Pending", value: "pending" },
                      { label: "In Progress", value: "in_progress" },
                      { label: "Success", value: "success" },
                      { label: "Failed", value: "failed" },
                    ]}
                    onChange={(e) => handleExtraChange(row, "status", e.value)}
                    style={{ width: "100%", minWidth: 100 }}
                  />
                );
              }
              const colors = { pending: "#f59e0b", in_progress: "#3b82f6", success: "#22c55e", failed: "#ef4444" };
              return (
                <span style={{
                  fontSize: "11px", fontWeight: 600, color: colors[status] || "#64748b",
                  background: `${colors[status] || "#64748b"}18`, padding: "2px 8px", borderRadius: 4,
                }}>
                  {status === "in_progress" ? "In Progress" : status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              );
            }}
          />,
        ]}
        {tableType === "evolution" && (
          <Column field="designId" header="Design ID" sortable style={{ minWidth: 100, width: 100, maxWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => (
              <span className="subtable-id-cell">{row.designId || row.externalCompoundIds || "—"}</span>
            )}
          />
        )}
        {tableType === "activity" && [
          <Column key="entityId" field="entityId" header="Entity ID" sortable
            style={{ minWidth: 100, textAlign: "center", fontWeight: 600 }}
            headerStyle={{textAlign:"center"}}
            body={(row) => <span style={{ color: "#6366f1", fontWeight: 600 }}>{row.entityId || "—"}</span>}
          />,
          // Structure image (for activity, placed between Entity ID and pivot columns)
          <Column key="structure" header="Structure" style={{ minWidth: 90, width: 110, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => {
              const smi = row.canonicalSmile || row.smile;
              if (!smi) return <span style={{ color: "#cbd5e1", fontSize: "0.75rem" }}>—</span>;
              return (
                <div style={{ position: "relative", width: "100%", height: rowHeight, minHeight: rowHeight }}>
                  <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <canvas
                      id={`act-struct-${row.entityId || Math.random()}`}
                      className="subtable-structure-canvas"
                      data-smiles={smi}
                      style={{ display: "block", cursor: "pointer" }}
                      onClick={() => { setStructurePreviewSmiles(smi); setDisplayStructurePreview(true); }}
                    />
                  </div>
                  <div
                    className="subtable-row-resize-handle"
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const startY = e.clientY;
                      const startH = rowHeight;
                      const onMove = (ev) => {
                        const newH = Math.max(50, Math.min(300, startH + ev.clientY - startY));
                        setRowHeight(newH);
                      };
                      const onUp = () => {
                        document.removeEventListener('mousemove', onMove);
                        document.removeEventListener('mouseup', onUp);
                      };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  />
                </div>
              );
            }}
          />,
          // Dynamic pivot columns: one per AssayName — inline editable when editing
          ...pivotColumns.map(colName => (
            <Column key={colName} field={colName}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); toggleChartColumn(colName); if (!chartVisible || chartColumns.length === 0) openChartDialog(); }}>
                  <span style={{ fontSize: "0.8rem" }}>{colName}</span>
                  <i className="pi pi-chart-bar" style={{ fontSize: "0.7rem", color: chartColumns.includes(colName) ? "#6366f1" : "#94a3b8" }} />
                </div>
              }
              sortable
              style={{ minWidth: 100, textAlign: "center" }}
              headerStyle={{textAlign:"center", background: "#f0fdf4", fontWeight: 600, fontSize: "0.8rem"}}
              body={(row) => {
                const editing = activeEditRowId === (row.entityId || row.id);
                const cellVal = activityCellValues[colName] !== undefined ? activityCellValues[colName] : (row[colName] || "");
                if (editing) {
                  return (
                    <InputText
                      value={cellVal}
                      onChange={(e) => setActivityCellValues(prev => ({ ...prev, [colName]: e.target.value }))}
                      onBlur={async () => {
                        const v = activityCellValues[colName];
                        if (row._records && v !== undefined) {
                          try {
                            const values = [
                              { assayName: colName, value: v || "" },
                              ...pivotColumns.filter(c => c !== colName).map(c => ({
                                assayName: c,
                                value: activityCellValues[c] !== undefined ? activityCellValues[c] : (row[c] && row[c] !== "—" ? row[c] : "")
                              }))
                            ];
                            const meta = row._records[0] || {};
                            await agent.Compounds.subTable.saveActivityValues({
                              projectId,
                              compoundId: row.compoundId,
                              values: values.filter(v => v.value?.trim()),
                              condition: meta.condition || "",
                              pmid: meta.pmid || "",
                              notes: meta.notes || "",
                            });
                            setRows(prev => prev.map(r => r.entityId === row.entityId ? { ...r, [colName]: v || "—" } : r));
                            toast.current?.show?.({ severity: "success", summary: "Saved", life: 1500 });
                          } catch { /* ignore */ }
                        }
                      }}
                      style={{ width: "100%", fontSize: "0.8rem", textAlign: "center" }}
                    />
                  );
                }
                const displayVal = row[colName];
                return (
                  <span style={{ fontWeight: 600, color: displayVal && displayVal !== "—" ? "#059669" : "#cbd5e1" }}>
                    {displayVal || "—"}
                  </span>
                );
              }}
            />
          )),
        ]}
        {tableType !== "design" && tableType !== "synthesis" && tableType !== "evolution" && tableType !== "activity" && (
          <Column field="entityId" header="Entity ID" sortable style={{ minWidth: 100, width: 100, maxWidth: 100, textAlign: "center" }} headerStyle={{textAlign:"center"}}
            body={(row) => (
              <span className="subtable-id-cell">{row.entityId || row.externalCompoundIds || row.designId || "—"}</span>
            )}
          />
        )}
      {/* ── SMILES structure image (auto-sizing canvas) — hidden for activity (uses its own) */}
      {tableType !== "activity" && (
        <Column header="Structure" style={{ minWidth: 90, width: 110, textAlign: "center" }} headerStyle={{textAlign:"center"}}
          body={(row) => {
            if (!row.canonicalSmile && !row.smile) return <span style={{ color: "#cbd5e1", fontSize: "0.75rem" }}>—</span>;
            const dispSmile = row.canonicalSmile || row.smile;
            return (
              <div style={{ position: "relative", width: "100%", height: rowHeight, minHeight: rowHeight }}>
                <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <canvas
                    id={`subtable-structure-${row.id || Math.random()}`}
                    className="subtable-structure-canvas"
                    data-smiles={dispSmile}
                    style={{ display: "block", cursor: "pointer" }}
                    onClick={() => { setStructurePreviewSmiles(dispSmile); setDisplayStructurePreview(true); }}
                  />
                </div>
                {/* Ketcher edit button for Design page */}
                {tableType === "design" && (
                  <div style={{ position: "absolute", top: -2, right: -2 }}>
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-rounded p-button-text p-button-sm"
                      style={{ width: 20, height: 20, fontSize: "0.6rem", background: "rgba(99,102,241,0.1)", color: "#6366f1" }}
                      tooltip="Edit structure with Ketcher"
                      onClick={(e) => { e.stopPropagation(); openKetcher(row); }}
                    />
                  </div>
                )}
                <div
                  className="subtable-row-resize-handle"
                  onMouseDown={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const startY = e.clientY;
                    const startH = rowHeight;
                    const onMove = (ev) => {
                      const newH = Math.max(50, Math.min(300, startH + ev.clientY - startY));
                      setRowHeight(newH);
                    };
                    const onUp = () => {
                      document.removeEventListener('mousemove', onMove);
                      document.removeEventListener('mouseup', onUp);
                    };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                />
              </div>
            );
          }}
        />
        )}
        {/* SMILES text (prefer canonical) — hidden for activity */}
        {tableType !== "activity" && (
          <Column field="canonicalSmile" header="SMILES" sortable style={{ minWidth: 130 }}
            body={(row) => (
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: "0.8rem", color: "#334155" }}>
                {row.canonicalSmile || row.smile || "—"}
              </span>
            )}
          />
        )}
        {/* Dynamic custom columns from ExtraData — hidden for activity (uses assay columns) */}
        {tableType !== "activity" && columns.map((col) => (
          <Column key={col.columnName}
            field={`extra.${col.columnName}`}
            header={col.displayName || col.columnName}
            sortable
            style={{ minWidth: 110 }}
            body={extraTemplate(col)}
          />
        ))}
        {/* Actions */}
        <Column header="Actions" body={actionsTemplate} style={{ width: 90, textAlign: "center" }} />
      </DataTable></div>

      {/* ── Structure Preview Dialog ── */}
      <Dialog
        header="Molecular Structure"
        visible={displayStructurePreview}
        style={{ width: "480px" }}
        modal
        onHide={() => { setDisplayStructurePreview(false); setStructurePreviewSmiles(null); }}
        footer={
          <Button label="Close" icon="pi pi-times" className="p-button-text"
            onClick={() => { setDisplayStructurePreview(false); setStructurePreviewSmiles(null); }} />
        }
      >
        {structurePreviewSmiles && (
          <div className="flex justify-content-center p-3">
            <SmilesView smiles={structurePreviewSmiles} width={400} height={350} />
          </div>
        )}
      </Dialog>

      {/* ── Ketcher Molecule Editor Dialog (Design only) ── */}
      {tableType === "design" && (
        <KetcherDialog
          visible={ketcherVisible}
          initialSmiles={ketcherSmiles}
          onConfirm={handleKetcherConfirm}
          onClose={() => setKetcherVisible(false)}
        />
      )}

      {/* ── Edit Confirm Dialog ── */}
      <Dialog
        header="Molecule structure changed"
        visible={confirmEditVisible}
        style={{ width: "420px" }}
        modal
        draggable={false}
        onHide={() => setConfirmEditVisible(false)}
      >
        <div className="flex flex-column gap-3" style={{ padding: "8px 0" }}>
          <div style={{ fontSize: "0.9rem", color: "#374151", lineHeight: 1.5 }}>
            The molecular structure has been modified. How would you like to proceed?
          </div>
          <div style={{ display: "flex", gap: 8, fontFamily: "'Courier New', monospace", fontSize: "0.8rem", color: "#64748b", background: "#f8fafc", padding: 8, borderRadius: 6 }}>
            <span style={{ textDecoration: "line-through", color: "#ef4444" }}>{ketcherSmiles || "(empty)"}</span>
            <span style={{ color: "#94a3b8" }}>→</span>
            <span style={{ color: "#22c55e" }}>{newSmilesFromKetcher}</span>
          </div>
        </div>
        <div className="flex flex-column gap-2" style={{ marginTop: 12 }}>
          <Button
            label="Create as new molecule (recommended)"
            icon="pi pi-plus-circle"
            className="p-button-sm p-button-outlined"
            style={{ justifyContent: "flex-start", textAlign: "left" }}
            onClick={() => handleEditConfirm("create")}
          />
          <Button
            label="Overwrite current molecule"
            icon="pi pi-exclamation-triangle"
            className="p-button-sm p-button-outlined p-button-danger"
            style={{ justifyContent: "flex-start", textAlign: "left" }}
            onClick={() => handleEditConfirm("overwrite")}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={() => setConfirmEditVisible(false)}
          />
        </div>
      </Dialog>

      {/* ── Add Row Dialog ── */}
      <Dialog header={`${cfg.icon} Add ${cfg.label} Entry`} visible={showAddDialog}
        style={{ width: "500px" }} modal draggable={false}
        onHide={() => {
          setShowAddDialog(false); setAddId(""); setAddSmiles(""); setAddExtra("{}");
          setAddSelectedDesignIds([]); setAvailableDesignIds([]);
        }}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm"
              onClick={() => {
                setShowAddDialog(false); setAddId(""); setAddSmiles(""); setAddExtra("{}");
                setAddSelectedDesignIds([]); setAvailableDesignIds([]);
              }} />
            <Button label="Save" icon="pi pi-check" className="p-button-sm subtable-primary-btn"
              loading={saving} onClick={handleAddRow} />
          </div>
        }
      >
        <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
          {/* Batch-select from existing compounds (synthesis mode) */}
          {tableType === "synthesis" && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                Select from unscheduled compounds
              </label>
              <MultiSelect
                value={addSelectedDesignIds}
                options={availableDesignIds.map(d => ({ label: d, value: d }))}
                onChange={(e) => setAddSelectedDesignIds(e.value)}
                placeholder="Choose DesignIds..."
                style={{ width: "100%" }}
                display="chip"
              />
            </div>
          )}
          {cfg.needsId && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                {tableType === "design" ? "Design ID" : "Compound ID"} {cfg.needsSmiles ? "*" : ""}
              </label>
              <InputText value={addId} onChange={(e) => setAddId(e.target.value)}
                placeholder={cfg.canCreate ? "e.g. I-001" : "e.g. I-001 (must exist in Compounds)"}
                style={{ width: "100%" }} />
            </div>
          )}
          {cfg.needsSmiles && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                SMILES *
              </label>
              <InputText value={addSmiles} onChange={(e) => setAddSmiles(e.target.value)}
                placeholder="e.g. CCO" style={{ width: "100%" }} />
            </div>
          )}
          {columns.length > 0 && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                Custom Fields (JSON)
              </label>
              <InputTextarea value={addExtra} onChange={(e) => setAddExtra(e.target.value)}
                rows={4} style={{ width: "100%", fontSize: "0.8rem", fontFamily: "monospace" }}
                placeholder='{"column1": "value1", "column2": "value2"}' />
            </div>
          )}
        </div>
      </Dialog>

      {/* ── Column Management Dialog ── */}
      <Dialog header={`Manage Columns — ${cfg.label}`} visible={showColumnDialog}
        style={{ width: "450px" }} modal draggable={false}
        onHide={() => { setShowColumnDialog(false); setNewColumnName(""); setNewColumnDisplay(""); }}
      >
        <div className="flex flex-column gap-3">
          {columns.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic", textAlign: "center", padding: 12 }}>
              No custom columns yet. Add one below.
            </div>
          )}
          {columns.map((col, i) => (
            <div key={col.columnName} className="flex align-items-center gap-2 p-2 border-round"
              style={{ background: i % 2 === 0 ? "#f8fafc" : "transparent" }}>
              <i className="pi pi-bars" style={{ color: "#94a3b8", fontSize: "0.75rem", cursor: "grab" }} />
              <span style={{ flex: 1, fontSize: "0.875rem" }}>
                <span style={{ fontWeight: 500 }}>{col.displayName || col.columnName}</span>
                <span style={{ color: "#94a3b8", fontSize: "0.75rem", marginLeft: 8 }}>({col.columnName})</span>
              </span>
              <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                onClick={() => handleRemoveColumn(col.columnName)} />
            </div>
          ))}
          <div style={{ borderTop: "1px solid #e2e8f0", margin: "8px 0", paddingTop: 12 }}>
            <div className="flex flex-column gap-2">
              <InputText placeholder="Column name (internal, e.g. ic50_value)" value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)} />
              <InputText placeholder="Display name (e.g. IC50 (µM))" value={newColumnDisplay}
                onChange={(e) => setNewColumnDisplay(e.target.value)} />
              <Button label="Add Column" icon="pi pi-plus" className="p-button-sm subtable-primary-btn"
                onClick={handleAddColumn}
                disabled={!newColumnName.trim() || !newColumnDisplay.trim()} />
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Activity Add/Edit Dialog ── */}
      {tableType === "activity" && (
        <Dialog
          header={editingActivityRow ? "✏️ Edit Activity Entry" : "🔬 Add Activity Entry"}
          visible={showActivityDialog}
          style={{ width: "520px" }}
          modal draggable={false}
          onHide={() => { setShowActivityDialog(false); setEditingActivityRow(null); }}
          footer={
            <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
              <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm"
                onClick={() => { setShowActivityDialog(false); setEditingActivityRow(null); }} />
              <Button label="Save" icon="pi pi-check" className="p-button-sm subtable-primary-btn"
                loading={activitySaving} onClick={handleActivitySave} />
            </div>
          }
        >
          <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
            {!editingActivityRow && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Entity ID <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <Dropdown
                  value={activityForm.entityId}
                  options={availableEntities.map(e => ({ label: e.entityId, value: e.entityId }))}
                  onChange={(e) => {
                    const ent = availableEntities.find(a => a.entityId === e.value);
                    handleActivityFormChange("entityId", e.value);
                    handleActivityFormChange("compoundId", ent?.id || "");
                  }}
                  placeholder="Select compound..."
                  style={{ width: "100%" }}
                />
                <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>
                  Only compounds with EntityId not yet in Activity are shown
                </div>
              </div>
            )}
            {editingActivityRow && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Entity ID</label>
                <InputText value={editingActivityRow.entityId || ""} disabled style={{ width: "100%" }} />
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Assay Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <InputText value={activityForm.assayName}
                  onChange={(e) => handleActivityFormChange("assayName", e.target.value)}
                  placeholder="IC50" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Assay Type <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <InputText value={activityForm.assayType}
                  onChange={(e) => handleActivityFormChange("assayType", e.target.value)}
                  placeholder="Primary" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Value <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <InputText value={activityForm.value}
                  onChange={(e) => handleActivityFormChange("value", e.target.value)}
                  placeholder="12.5" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Unit <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <InputText value={activityForm.unit}
                  onChange={(e) => handleActivityFormChange("unit", e.target.value)}
                  placeholder="nM" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
                  Method <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <InputText value={activityForm.method}
                  onChange={(e) => handleActivityFormChange("method", e.target.value)}
                  placeholder="ELISA" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Condition</label>
                <InputText value={activityForm.condition}
                  onChange={(e) => handleActivityFormChange("condition", e.target.value)}
                  placeholder="10uM compound" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Target Name</label>
                <InputText value={activityForm.targetName}
                  onChange={(e) => handleActivityFormChange("targetName", e.target.value)}
                  placeholder="KRAS" style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>PMID</label>
                <InputText value={activityForm.pmid}
                  onChange={(e) => handleActivityFormChange("pmid", e.target.value)}
                  placeholder="12345678" style={{ width: "100%" }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Notes</label>
              <InputTextarea value={activityForm.notes}
                onChange={(e) => handleActivityFormChange("notes", e.target.value)}
                rows={3} style={{ width: "100%", fontSize: "0.8rem" }} />
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Activity Sub-List Dialog (records for one entity) ── */}
      <Dialog
        header={`📋 Activity Records — ${subListEntityLabel}`}
        visible={showActivitySubList}
        style={{ width: "800px" }}
        modal draggable={false}
        onHide={() => { setShowActivitySubList(false); setActivitySubRecords([]); }}
        footer={
          <Button label="Close" icon="pi pi-times" className="p-button-text"
            onClick={() => { setShowActivitySubList(false); setActivitySubRecords([]); }} />
        }
      >
        <DataTable value={activitySubRecords} size="small" stripedRows scrollable
          style={{ maxHeight: "400px" }}
          emptyMessage={<span style={{ color: "#94a3b8" }}>No records</span>}
        >
          <Column field="assayName" header="Assay Name" style={{ minWidth: 100 }} />
          <Column field="assayType" header="Type" style={{ minWidth: 80 }} />
          <Column field="value" header="Value" style={{ minWidth: 70, textAlign: "center" }}
            body={(r) => <span style={{ fontWeight: 600, color: "#059669" }}>{r.value}</span>} />
          <Column field="unit" header="Unit" style={{ minWidth: 60, textAlign: "center" }} />
          <Column field="method" header="Method" style={{ minWidth: 90 }} />
          <Column field="targetName" header="Target" style={{ minWidth: 80 }} />
          <Column field="condition" header="Condition" style={{ minWidth: 100 }} />
          <Column field="pmid" header="PMID" style={{ minWidth: 80 }} />
          <Column field="notes" header="Notes" style={{ minWidth: 100 }} />
          <Column header="" style={{ minWidth: 60, textAlign: "center" }}
            body={(r) => (
              <Button icon="pi pi-pencil" className="p-button-rounded p-button-info p-button-text p-button-sm"
                tooltip="Edit this record"
                onClick={() => { setShowActivitySubList(false); openActivityDialog(null, r); }} />
            )}
          />
        </DataTable>
      </Dialog>

      {/* ── Manage Assay Columns Dialog (Activity only) ── */}
      {tableType === "activity" && (
        <Dialog header="📊 Manage Assay Columns" visible={showManageColumns}
          style={{ width: "520px" }} modal draggable={false}
          onHide={() => setShowManageColumns(false)}
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
              <div>
                {columnOrderChanged && (
                  <Button label="💾 Save Order" icon="pi pi-save" className="p-button-sm p-button-success"
                    onClick={handleSaveOrder} />
                )}
              </div>
              <Button label="Close" icon="pi pi-times" className="p-button-text"
                onClick={() => setShowManageColumns(false)} />
            </div>
          }
        >
          <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Existing Columns
              </label>
              {assayColumns.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "8px 0" }}>No columns defined yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {assayColumns.map(col => (
                    <div key={col.id}
                      draggable
                      onDragStart={() => handleDragStart(col.id)}
                      onDragOver={(e) => handleDragOver(e, col.id)}
                      onDrop={handleDrop}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 8px", background: dragColId === col.id ? "#e0e7ff" : "#f8fafc", borderRadius: 6, cursor: "grab", border: dragColId === col.id ? "2px dashed #6366f1" : "2px solid transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <i className="pi pi-bars" style={{ color: "#94a3b8", fontSize: "0.75rem", cursor: "grab" }} />
                        <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>{col.assayName}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                          tooltip="Edit column" onClick={() => handleEditColumnOpen(col)}
                          style={{ width: 24, height: 24, color: "#64748b" }} />
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                          tooltip="Delete column" onClick={() => handleDeleteColumn(col.id)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Collapsible Add New Column */}
            {columnOrderChanged && (
              <div style={{ fontSize: "0.8rem", color: "#f59e0b", textAlign: "center", padding: "2px 0" }}>
                ⚠️ Order changed — click 💾 Save Order to persist
              </div>
            )}

            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
              <div
                onClick={() => setShowNewColumnSection(!showNewColumnSection)}
                style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, userSelect: "none", padding: "4px 0" }}
              >
                <i className={`pi ${showNewColumnSection ? "pi-chevron-down" : "pi-chevron-right"}`}
                  style={{ fontSize: "0.75rem", color: "#64748b" }} />
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                  Add New Column
                </label>
              </div>

              {showNewColumnSection && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Method *</label>
                      <InputText value={newColumnForm.method}
                        onChange={(e) => handleNewColumnFieldChange("method", e.target.value)}
                        placeholder="TR-FRET" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Assay Type *</label>
                      <InputText value={newColumnForm.assayType}
                        onChange={(e) => handleNewColumnFieldChange("assayType", e.target.value)}
                        placeholder="Affinity-Ki" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Target Name *</label>
                      <InputText value={newColumnForm.targetName}
                        onChange={(e) => handleNewColumnFieldChange("targetName", e.target.value)}
                        placeholder="BRD4 BD1" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Unit *</label>
                      <InputText value={newColumnForm.unit}
                        onChange={(e) => handleNewColumnFieldChange("unit", e.target.value)}
                        placeholder="nM" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Condition <span style={{ color: "#94a3b8" }}>(optional)</span></label>
                      <InputText value={newColumnForm.condition}
                        onChange={(e) => handleNewColumnFieldChange("condition", e.target.value)}
                        placeholder="1% DMSO, 1h pre-incubation" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>PMID <span style={{ color: "#94a3b8" }}>(optional)</span></label>
                      <InputText value={newColumnForm.pmid}
                        onChange={(e) => handleNewColumnFieldChange("pmid", e.target.value)}
                        placeholder="12345678" style={{ width: "100%", fontSize: "0.85rem" }} />
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Note <span style={{ color: "#94a3b8" }}>(optional)</span></label>
                    <InputTextarea value={newColumnForm.note}
                      onChange={(e) => handleNewColumnFieldChange("note", e.target.value)}
                      placeholder="Any additional notes..." style={{ width: "100%", fontSize: "0.85rem" }} rows={2} />
                  </div>
                  {newColumnForm._assayName && (
                    <div style={{ fontSize: "0.8rem", color: "#6366f1", marginTop: 6, fontFamily: "monospace" }}>
                      → {newColumnForm._assayName}
                    </div>
                  )}
                  <Button label="+ Add Column" icon="pi pi-plus" className="p-button-sm subtable-primary-btn"
                    style={{ marginTop: 8 }}
                    loading={columnSaving} onClick={handleAddAssayColumn} />
                </div>
              )}
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Edit Column Dialog ── */}
      {tableType === "activity" && editingColumn && (
        <Dialog header={`✏️ Edit Column: ${editingColumn.assayName}`} visible={!!editingColumn}
          style={{ width: "480px" }} modal draggable={false}
          onHide={() => setEditingColumn(null)}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button label="Cancel" icon="pi pi-times" className="p-button-text"
                onClick={() => setEditingColumn(null)} />
              <Button label="Save" icon="pi pi-check" className="p-button-sm p-button-success"
                loading={columnSaving} onClick={handleEditColumnSave} />
            </div>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "4px 0" }}>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Method</label>
              <InputText value={editingColumn._method}
                onChange={(e) => handleEditColumnFieldChange("_method", e.target.value)}
                placeholder="TR-FRET" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Assay Type</label>
              <InputText value={editingColumn._assayType}
                onChange={(e) => handleEditColumnFieldChange("_assayType", e.target.value)}
                placeholder="Affinity-Ki" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Target Name</label>
              <InputText value={editingColumn._targetName}
                onChange={(e) => handleEditColumnFieldChange("_targetName", e.target.value)}
                placeholder="BRD4 BD1" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Unit</label>
              <InputText value={editingColumn._unit}
                onChange={(e) => handleEditColumnFieldChange("_unit", e.target.value)}
                placeholder="nM" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Condition <span style={{ color: "#94a3b8" }}>(optional)</span></label>
              <InputText value={editingColumn._condition}
                onChange={(e) => handleEditColumnFieldChange("_condition", e.target.value)}
                placeholder="1% DMSO, 1h pre-incubation" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>PMID <span style={{ color: "#94a3b8" }}>(optional)</span></label>
              <InputText value={editingColumn._pmid}
                onChange={(e) => handleEditColumnFieldChange("_pmid", e.target.value)}
                placeholder="12345678" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 2 }}>Note <span style={{ color: "#94a3b8" }}>(optional)</span></label>
            <InputTextarea value={editingColumn._note}
              onChange={(e) => handleEditColumnFieldChange("_note", e.target.value)}
              placeholder="Any additional notes..." style={{ width: "100%", fontSize: "0.85rem" }} rows={2} />
          </div>
          {editingColumn._assayNamePreview && (
            <div style={{ fontSize: "0.8rem", color: "#6366f1", marginTop: 8, fontFamily: "monospace", textAlign: "center" }}>
              → {editingColumn._assayNamePreview}
            </div>
          )}
        </Dialog>
      )}

      {/* ── Bar Chart Compare Dialog ── */}
      {tableType === "activity" && (
        <Dialog header="📊 Bar Chart Compare" visible={chartVisible}
          style={{ width: "760px" }} modal
          onHide={() => setChartVisible(false)}
          footer={
            <Button label="Close" icon="pi pi-times" className="p-button-text"
              onClick={() => setChartVisible(false)} />
          }
        >
          <div style={{ padding: "4px 0" }}>
            {/* Column chips */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                Selected Columns:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {chartColumns.map(col => (
                  <span key={col} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: "#e0e7ff", borderRadius: 12, fontSize: "0.8rem", color: "#4338ca" }}>
                    {col}
                    <i className="pi pi-times" style={{ cursor: "pointer", fontSize: "0.65rem" }} onClick={() => toggleChartColumn(col)} />
                  </span>
                ))}
                {pivotColumns.filter(c => !chartColumns.includes(c)).length > 0 && (
                  <Dropdown
                    value={null}
                    options={pivotColumns.filter(c => !chartColumns.includes(c)).map(c => ({ label: c, value: c }))}
                    onChange={(e) => toggleChartColumn(e.value)}
                    placeholder="+ Add Column"
                    style={{ minWidth: 100, height: 26, fontSize: "0.8rem" }}
                  />
                )}
              </div>
            </div>
            {/* Molecule chips */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                Selected Molecules:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {chartMolecules.map(eid => (
                  <span key={eid} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: "#fef3c7", borderRadius: 12, fontSize: "0.8rem", color: "#92400e" }}>
                    {eid}
                    <i className="pi pi-times" style={{ cursor: "pointer", fontSize: "0.65rem" }} onClick={() => toggleChartMolecule(eid)} />
                  </span>
                ))}
                {(pivotRows.filter(r => r.entityId && !chartMolecules.includes(r.entityId)).length > 0) && (
                  <Dropdown
                    value={null}
                    options={pivotRows.filter(r => r.entityId && !chartMolecules.includes(r.entityId)).map(r => ({ label: r.entityId, value: r.entityId }))}
                    onChange={(e) => toggleChartMolecule(e.value)}
                    placeholder="+ Add Molecule"
                    style={{ minWidth: 120, height: 26, fontSize: "0.8rem" }}
                  />
                )}
              </div>
            </div>
            {/* Settings bar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8, padding: "6px 8px", background: "#f8fafc", borderRadius: 6, alignItems: "center" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Settings:</label>
              <label style={{ fontSize: 11, color: "#64748b" }}>H</label>
              <input type="range" min={200} max={500} value={chartConfig.height}
                onChange={(e) => setChartConfig(p => ({ ...p, height: +e.target.value }))}
                style={{ width: 60 }} />
              <label style={{ fontSize: 11, color: "#64748b" }}>W</label>
              <input type="range" min={10} max={50} value={chartConfig.barWidth}
                onChange={(e) => setChartConfig(p => ({ ...p, barWidth: +e.target.value }))}
                style={{ width: 60 }} />
              <label style={{ fontSize: 11, color: "#64748b" }}>Gap</label>
              <input type="range" min={0} max={20} value={chartConfig.barGap}
                onChange={(e) => setChartConfig(p => ({ ...p, barGap: +e.target.value }))}
                style={{ width: 60 }} />

              <div style={{ borderLeft: "1px solid #e2e8f0", height: 20, margin: "0 4px" }} />

              <label style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 3 }}>
                <input type="checkbox" checked={chartConfig.showGrid} onChange={(e) => setChartConfig(p => ({ ...p, showGrid: e.target.checked }))} /> Grid
              </label>
              <label style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 3 }}>
                <input type="checkbox" checked={chartConfig.showTooltip} onChange={(e) => setChartConfig(p => ({ ...p, showTooltip: e.target.checked }))} /> Tooltip
              </label>
              <label style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 3 }}>
                <input type="checkbox" checked={chartConfig.showLegend} onChange={(e) => setChartConfig(p => ({ ...p, showLegend: e.target.checked }))} /> Legend
              </label>
              <label style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 3 }}>
                <input type="checkbox" checked={chartConfig.showLabel} onChange={(e) => setChartConfig(p => ({ ...p, showLabel: e.target.checked }))} /> Label
              </label>

              <div style={{ borderLeft: "1px solid #e2e8f0", height: 20, margin: "0 4px" }} />

              <label style={{ fontSize: 11, color: "#64748b" }}>Legend:</label>
              <select value={chartConfig.legendPos} onChange={(e) => setChartConfig(p => ({ ...p, legendPos: e.target.value }))}
                style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #d1d5db" }}>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </div>
            {/* Chart */}
            {chartData.length > 0 ? (
              <div style={{ background: "#fff", borderRadius: 8, padding: 8, overflowX: "auto" }}>
                <SimpleBarChart
                  data={chartData}
                  xKey="entityId"
                  yKey="value"
                  yLabel="Value"
                  groupKey="assayName"
                  colors={["#6366f1", "#22c55e", "#eab308", "#ef4444", "#06b6d4", "#f97316", "#a855f7", "#14b8a6"]}
                  config={chartConfig}
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                <i className="pi pi-chart-bar" style={{ fontSize: "2rem", display: "block", marginBottom: 8 }} />
                Click 📊 on column headers to add data
              </div>
            )}
          </div>
        </Dialog>
      )}

    </div>
  );
});

export default SubTableView;
